import React, { useState } from "react";
import { UserCreate } from "../../../models/users";
import { sendEmailtoUser } from "../../../api/mail";
import toast from "react-hot-toast";

interface CreateUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (user: UserCreate) => void;
}

export default function CreateUserModal({ isOpen, onClose, onConfirm }: CreateUserModalProps) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [role, setRole] = useState("user");
    const [touched, setTouched] = useState<{ [key: string]: boolean }>({});

    if (!isOpen) return null;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isNameValid = name.trim().length > 0;
    const isEmailValid = emailRegex.test(email);
    const isPasswordValid = password.length >= 6 && password.length <= 50;
    const isValid = isNameValid && isEmailValid && isPasswordValid;

    const showError = (field: string) => touched[field];
    const handleSubmit = async () => {
        try {
            setTouched({ name: true, email: true, password: true });
            if (!isValid) return;
            onConfirm({ name, email, password, role });
            if (role == "user") {
                await handleNotify({ name, email, password, role });
            }
            setName("");
            setEmail("");
            setPassword("");
            setRole("user");
            setTouched({});
            onClose();
        }
        catch (err: any) {
            toast.error(err.response?.data?.detail || "Failed to notify user");
        }
    };

    const handleNotify = async ({ name, email, password, role }: { name: string, email: string, password: string, role: string }) => {
        try {
            let body = `
<!DOCTYPE html>
<html lang="en" dir="ltr">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>RPM Credentials</title>
<style>
  a.es-button{text-decoration:none!important}
  .adapt-img{width:100%;height:auto}
  @media only screen and (max-width:600px){
    .es-left,.es-right,.es-content-body{width:100%!important;display:block!important}
  }
</style>
</head>

<body style="margin:0;padding:0;font-family:Arial,sans-serif;background:#fafafa">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#fafafa">
<tr><td align="center">

<table width="600" cellpadding="0" cellspacing="0" style="background:#fff;width:600px">
<tr>
  <td style="background:#666;padding:20px;text-align:center">
    <h2 style="color:#ed6b20;font-size:46px;margin:0;font-weight:bold">RPM MARKETPLACE</h2>
    <h2 style="color:#fff;font-size:46px;margin:0;font-weight:bold">LOGIN CREDENTIALS</h2>
  </td>
</tr>

<tr>
<td style="background:#666;padding:20px;color:#fff">

  <table align="left" width="173" cellpadding="0" cellspacing="0">
    <tr>
      <td style="font-size:0">
        <a href="https://marketplace.rpmleader.com" target="_blank">
          <img class="adapt-img" src="https://marketplace.rpmleader.com/media/thumbnail_marketplace.png" width="173" style="display:block;border:0" />
        </a>
      </td>
    </tr>
  </table>

  <table align="right" width="350" cellpadding="0" cellspacing="0" style="color:#fff;font-size:15px;line-height:22px">
    <tr><td>

      <p>Hello ${name || email},</p>
      <p>Here are your <strong style="color:#ed6b20">RPM Marketplace</strong> credentials:</p>

     <div style="padding-left:10px">
  <p style="margin:0;line-height:20px">Username: <strong style="color:#ed6b20">${email}</strong></p>
  <p style="margin:0;line-height:20px">Password: <strong style="color:#ed6b20">${password}</strong></p>
</div>
    <p>Marketplace: <a href="https://marketplace.rpmleader.com" style="color:#ed6b20; text-decoration:underline">marketplace.rpmleader.com</a>
    </p>
      <p>Please do not share your credentials with anyone.</p>
      <p>If you face any issue or want to change your password, contact <strong style="color:#ed6b20">tech@rpmleader.com</strong>.</p>

      <p>Thanks,<br/>RPM Tech Team</p>

    </td></tr>
  </table>

</td>
</tr>

</table>

</td></tr>
</table>
</body>
</html>
`;


            await sendEmailtoUser({
                subject: "RPM Marketplace - Login Credentials",
                body,
                receiving_email: email,
            });

            toast.success(`Notification sent to ${email} `);
        } catch (err: any) {
            toast.error(err.response?.data?.detail || "Failed to notify user");
        }
    };


    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-96 shadow-lg">
                <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                    Create User
                </h2>

                {/* Name */}
                <div className="mb-3">
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                        Name
                    </label>
                    <input
                        type="text"
                        placeholder="Full name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={() => setTouched(t => ({ ...t, name: true }))}
                        className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white"
                    />
                    {showError("name") && !isNameValid && (
                        <p className="text-red-500 text-sm mt-1">Name is required.</p>
                    )}
                </div>

                {/* Email */}
                <div className="mb-3">
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                        Email
                    </label>
                    <input
                        type="email"
                        placeholder="Email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onBlur={() => setTouched(t => ({ ...t, email: true }))}
                        className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white"
                    />
                    {showError("email") && !isEmailValid && (
                        <p className="text-red-500 text-sm mt-1">Enter a valid email.</p>
                    )}
                </div>

                {/* Password */}
                <div className="mb-3">
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                        Password
                    </label>
                    <input
                        type="password"
                        placeholder="6–50 characters"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onBlur={() => setTouched(t => ({ ...t, password: true }))}
                        className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white"
                    />
                    {showError("password") && !isPasswordValid && (
                        <p className="text-red-500 text-sm mt-1">
                            Password must be between 6 and 50 characters.
                        </p>
                    )}
                </div>

                {/* Role */}
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                        Role
                    </label>
                    <select
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white"
                    >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="superadmin">Super Admin</option>
                    </select>
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!isValid}
                        className={`px-4 py-2 rounded text-white ${isValid ? "bg-blue-600 hover:bg-blue-700" : "bg-blue-400 cursor-not-allowed"}`}
                    >
                        Create
                    </button>
                </div>
            </div>
        </div>
    );
}
